import React, { useEffect, useRef } from "react";
import Globe from "react-globe.gl";
import TwinklingStars from "./Stars";

const HeroSection = () => {
  const globeEl = useRef();

  useEffect(() => {
    if (globeEl.current) {
      globeEl.current.controls().autoRotate = true;
      globeEl.current.controls().autoRotateSpeed = 0.6;
      globeEl.current.pointOfView({ lat: 0, lng: 0, altitude: 1.5 });
    }

    const handleResize = () => {
      if (globeEl.current) {
        globeEl.current.width =
          window.innerWidth < 768
            ? window.innerWidth * 0.9
            : window.innerWidth * 0.6;
        globeEl.current.height =
          window.innerWidth < 768
            ? window.innerHeight * 0.5
            : window.innerHeight * 0.6;
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const myData = [
    // optional globe data points
  ];

  return (
    <section className="relative bg-black text-orange-500 overflow-hidden py-16 h-fit">
      {/* 🌍 Globe Centered on Mobile */}
      <div className="absolute inset-0 z-0 flex items-center justify-center md:justify-start">
        <Globe
          ref={globeEl}
          width={
            window.innerWidth < 768
              ? window.innerWidth * 0.9
              : window.innerWidth * 0.6
          }
          height={
            window.innerWidth < 768
              ? window.innerHeight * 0.5
              : window.innerHeight * 0.6
          }
          globeImageUrl="//unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
          pointsData={myData}
        />
      </div>

      {/* ✨ Stars */}
      <div className="absolute top-0 right-0 w-full h-full flex items-center justify-center">
        <TwinklingStars />
      </div>

      {/* 📄 Content */}
      <div className="container mx-auto px-4 py-24 relative z-10 flex flex-col md:flex-row items-center">
        {/* Left Side: Company Info */}
        <div className="w-full md:w-1/2 mb-12 md:mb-0 flex flex-col justify-center text-center md:text-left md:pl-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-8 leading-tight text-orange-700">
            Mindspark Revolution.
            <br />
            Ignite Your Future.
            <br />
            Transform Tomorrow.
          </h1>
          <p className="text-lg mb-6 text-orange-300">
            At Mindspark Revolution, we are dedicated to pioneering solutions
            that drive progress and create value. Our expert team leverages
            cutting-edge technologies to craft innovative strategies that will
            propel your business forward.
          </p>
          <p className="text-lg mb-8 text-orange-300">
            Our commitment to excellence ensures that every project is executed
            with precision and creativity, delivering results that exceed
            expectations. Partner with us to unlock new possibilities and
            achieve remarkable success.
          </p>
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 justify-center md:justify-start">
            <a
              onClick={() => {
                const workflowSection = document.getElementById("service");
                workflowSection?.scrollIntoView({ behavior: "smooth" });
              }}
              className="bg-orange-500 text-black font-semibold px-8 py-3 rounded-full hover:bg-orange-600 transition duration-300 text-center"
            >
              Get Started
            </a>
            <a
              onClick={() => {
                const workflowSection = document.getElementById("workflow");
                workflowSection?.scrollIntoView({ behavior: "smooth" });
              }}
              className="bg-black text-orange-500 font-semibold px-8 py-3 rounded-full border border-orange-500 hover:bg-orange-500 hover:text-black transition duration-300 text-center"
            >
              Learn More
            </a>
          </div>
        </div>

        {/* Right Side: Features */}
        <div className="w-full md:w-1/2 md:pl-12 mt-12 md:mt-0">
          <div className="bg-white bg-opacity-20 backdrop-filter backdrop-blur-lg rounded-xl p-8 shadow-2xl">
            <h2 className="text-2xl font-semibold mb-6 text-gray-200 text-center md:text-left">
              Why Choose Mindspark Revolution?
            </h2>
            <ul className="space-y-4">
              <li className="flex items-center">
                <svg
                  className="w-6 h-6 mr-3 text-yellow-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  ></path>
                </svg>
                <span className="bg-clip-text text-transparent bg-white">
                  Lightning-fast Performance
                </span>
              </li>
              <li className="flex items-center">
                <svg
                  className="w-6 h-6 mr-3 text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  ></path>
                </svg>
                <span className="bg-clip-text text-transparent bg-white">
                  Budget Friendly
                </span>
              </li>
              <li className="flex items-center">
                <svg
                  className="w-6 h-6 mr-3 text-purple-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
                  ></path>
                </svg>
                <span className="bg-clip-text text-transparent bg-white">
                  AI-powered Insights
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* 🟢 Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden">
        <svg
          viewBox="0 0 1440 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 100L60 85C120 70 240 40 360 25C480 10 600 10 720 17.5C840 25 960 40 1080 47.5C1200 55 1320 55 1380 55L1440 55V100H0Z"
            fill="black"
          />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
